/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 29th 2024, 10:50:56 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadIcon from "@mui/icons-material/Upload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Edit as EditIcon, Trash2 as TrashIcon } from "react-feather";
import { DateTimeWithDistance } from "common/components/DateTimeWithDistance";
import { useMediaLibraryFileAPI } from "media-library/services/MediaLibraryAPI";
import { useMediaLibraryStore, mediaLibraryStoreSelectors } from "media-library/store";
import { MediaLibrary, MediaLibraryFile } from "lib/schemas";
import { useCopyTextToClipBoard, formatBytes } from "lib/utils";
import {
  Image,
  OSActionButton,
  StyledOSDialog,
  PopupMenu,
  APIListProgressWrapper,
  TextEditToggler,
  AlertSnackBar,
} from "ui-widgets";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { AssetListSkeleton } from "./AssetListSkeleton";
import { EmptyLibraryCTA } from "./EmptyLibraryCTA";
import { LibraryAssetsTopBar } from "./LibraryAssetsTopBar";
import { LibraryAssetUploadForm } from "./LibraryAssetUploadForm";
import { LibraryItemContainer } from "./LibraryItemContainer";
import { TOnChooseMediaAsset } from "./types";

interface LibraryFilesProps {
  mediaLibraryId: MediaLibrary["id"];
  onChooseAsset?: TOnChooseMediaAsset;
}

export const LibraryFiles = ({ mediaLibraryId, onChooseAsset }: LibraryFilesProps) => {
  const { useList } = useMediaLibraryFileAPI(mediaLibraryId);
  const { makeRequest: fetchFiles, isMakingAPIRequest, isAPIError, httpResponseStatusCode } = useList();

  const { populateFiles } = useMediaLibraryStore();
  const libraryFiles = useMediaLibraryStore(mediaLibraryStoreSelectors.getLibrary(mediaLibraryId))?.mediaLibraryFiles;

  React.useEffect(() => {
    if (libraryFiles?.length) return;

    fetchFiles({ setValidAPIResponse: (mediaLibraryFiles) => populateFiles(mediaLibraryId, mediaLibraryFiles) });
  }, [fetchFiles, populateFiles, libraryFiles?.length, mediaLibraryId]);
  return (
    <Stack spacing={4}>
      <LibraryAssetsTopBar
        title="Files"
        description="Upload files into your media library for easy reuse by you and your team."
        newUploadAction={
          <StyledOSDialog
            popUpTitle="Upload Files"
            popUpIcon={<InsertDriveFileIcon color="primary" />}
            renderOpenButton={({ handleOpen }) => (
              <OSActionButton
                sx={{ marginLeft: "auto", alignSelf: "flex-start" }}
                variant="contained"
                actionStyle="custom"
                customIcon={<UploadIcon />}
                onClick={handleOpen}
              >
                Upload Files
              </OSActionButton>
            )}
            renderContentWithClose={({ handleClose }) => (
              <FileUploadForm mediaLibraryId={mediaLibraryId} handleSaveDone={handleClose} />
            )}
          />
        }
      />
      <Grid container spacing={2}>
        <APIListProgressWrapper
          showLoader={isMakingAPIRequest}
          isAPIError={isAPIError}
          items={libraryFiles}
          httpResponseStatusCode={httpResponseStatusCode}
          loaderType="custom"
          customProgressLoader={<AssetListSkeleton />}
          noDataMessage={<EmptyLibraryCTA mediaType="file" />}
        >
          {libraryFiles?.map((fileRecord) => {
            const onClickFile = onChooseAsset
              ? () => {
                  onChooseAsset({
                    media_library_id: mediaLibraryId,
                    media_type: "file",
                    media_item_id: fileRecord.id,
                    media_item_url: fileRecord.file,
                    media_item_name: fileRecord.name,
                  });
                }
              : undefined;
            return (
              <Grid key={`${fileRecord.id}`} item xs={12} md={4} xl={3}>
                <LibraryFileCard fileRecord={fileRecord} mediaLibraryId={mediaLibraryId} onClick={onClickFile} />
              </Grid>
            );
          })}
        </APIListProgressWrapper>
      </Grid>
    </Stack>
  );
};

interface LibraryFileCardProps {
  fileRecord: MediaLibraryFile;
  mediaLibraryId: MediaLibrary["id"];
  onClick?: () => void;
}

const LibraryFileCard = ({ fileRecord, mediaLibraryId, onClick }: LibraryFileCardProps) => {
  const { useDelete, useMutate } = useMediaLibraryFileAPI(mediaLibraryId);
  const { patchData } = useMutate();

  const { removeFile, addFile } = useMediaLibraryStore();
  const [editName, setEditName] = React.useState(false);

  const handleEditFileName = (fileRecordId: MediaLibraryFile["id"], newName: string) => {
    patchData({
      apiPathParams: [fileRecordId],
      body: JSON.stringify({ name: newName }),
      setValidAPIResponse: (mutatedFileRecord: MediaLibraryFile) => {
        addFile(mediaLibraryId, mutatedFileRecord);
      },
    });
  };

  const { copied, copyTextToClipBoard } = useCopyTextToClipBoard();
  const [showCopiedAlert, setShowCopiedAlert] = React.useState(false);

  return (
    <>
      <AlertSnackBar
        open={copied && showCopiedAlert}
        message={`URL for ${fileRecord.name} copied to clipboard.`}
        severity="success"
        vertical={"top"}
        horizontal={"center"}
        handleClose={() => setShowCopiedAlert(false)}
        canBeClosedByUser={false}
        autoClose
      />
      <LibraryItemContainer onClick={onClick}>
        <Image
          src={getFileIconSrc(fileRecord.file)}
          alt={fileRecord.name}
          style={{ height: "150px", objectFit: "scale-down" }}
        />
      </LibraryItemContainer>

      <Stack direction="row">
        <TextEditToggler
          handleEdit={(newName) => (newName === fileRecord.name ? null : handleEditFileName(fileRecord.id, newName))}
          value={fileRecord.name}
          forceEditMode={editName}
          setForceEditMode={setEditName}
        />

        <div>
          <DeleteOSItemDialog<MediaLibraryFile>
            itemId={fileRecord.id}
            useDeleteOSItem={useDelete}
            onDelete={() => removeFile(mediaLibraryId, fileRecord.id)}
            onRestore={(restoredFileRecord) => addFile(mediaLibraryId, restoredFileRecord)}
          >
            {({ handleOpen: handleOpenDeleteFileRecord }) => (
              <PopupMenu
                menuItems={[
                  {
                    icon: <EditIcon size="16" />,
                    onClick: () => setEditName(true),
                    text: "Rename File",
                  },
                  {
                    icon: <ContentCopyIcon />,
                    onClick: () => {
                      copyTextToClipBoard(fileRecord.file);
                      setShowCopiedAlert(true);
                    },
                    text: "Copy Link",
                  },
                  {
                    icon: <TrashIcon size="16" />,
                    onClick: handleOpenDeleteFileRecord,
                    text: "Delete File",
                  },
                ]}
              />
            )}
          </DeleteOSItemDialog>
        </div>
      </Stack>
      <Typography variant="body1" color="text.secondary">
        <DateTimeWithDistance prefix={<>Added</>} dateTime={fileRecord.created_at} />
      </Typography>
    </>
  );
};

const validFileTypes = {
  pdf: ["application/pdf"],
  csv: ["text/csv"],
  excel: ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  word: ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
};

const getFileIconSrc = (fileURL: string) => {
  if (fileURL.includes(".pdf")) return "/img/pdf.png";
  if (fileURL.includes(".doc") || fileURL.includes(".docx")) return "/img/word.png";
  if (fileURL.includes(".xls") || fileURL.includes(".xlsx")) return "/img/excel.png";
  if (fileURL.includes(".csv")) return "/img/csv.png";
  return "/img/file.png";
};

const FileUploadForm = ({ mediaLibraryId, handleSaveDone }: { handleSaveDone?: () => void } & LibraryFilesProps) => {
  const { useAPIForm } = useMediaLibraryFileAPI(mediaLibraryId);
  const { addFile } = useMediaLibraryStore();
  const useAPIFormReturn = useAPIForm({
    id: null,
    onAPISave: (fileFromAPI) => {
      addFile(mediaLibraryId, fileFromAPI);
      handleSaveDone?.();
    },
    defaultValues: {
      file: null,
      library: mediaLibraryId,
    },
  });

  const onAddFiles = (files: File[]) => {
    useAPIFormReturn.useFormReturn.setValue("file", (files?.length > 0 ? files : null) as unknown as string);
  };

  const watchedFiles = useAPIFormReturn.useFormReturn.watch("file");
  const uploadedFiles = watchedFiles ? (Array.from(watchedFiles) as unknown as File[]) : null;

  return (
    <LibraryAssetUploadForm<MediaLibraryFile>
      useAPIFormReturn={useAPIFormReturn}
      uploadedFiles={uploadedFiles}
      onAddFiles={onAddFiles}
      multiple
      filePreviewProps={{
        validFileMessage: null,
        validFileIcon: (
          <Grid container spacing={2} sx={{ padding: "50px", minWidth: "500px" }}>
            {uploadedFiles?.map((uploadedFile, index) => (
              <Grid item xs={12} md={4} xl={3}>
                <Image src={getFileIconSrc(uploadedFile.name)} style={{ maxWidth: "100px" }} />
                <p>{uploadedFile?.name}</p>
                <Typography variant="subtitle1">
                  {formatBytes(uploadedFile?.size)} | {uploadedFile?.type}
                </Typography>
              </Grid>
            ))}
          </Grid>
        ),
        invalidFileMessage: null,
        invalidFileIcon: (
          <Grid container spacing={2} sx={{ padding: "50px", minWidth: "500px" }}>
            {uploadedFiles?.map((uploadedFile) => (
              <Grid item xs={12} md={4} xl={3}>
                <Image src={getFileIconSrc(uploadedFile.name)} style={{ maxWidth: "100px" }} />
                <p>
                  {uploadedFile?.name} is not a valid file type. Only PDFs, Word and Excel files are allowed.{" "}
                  {uploadedFile.type}
                </p>
              </Grid>
            ))}
          </Grid>
        ),
      }}
      isValid={uploadedFiles?.every((f) =>
        Object.values(validFileTypes)
          .reduce((prev, curr) => [...prev, ...curr], [])
          .includes(f.type)
      )}
    />
  );
};
